import { toDate } from '@/common/helpers/utilsDate'

import { ICambioEstadoReserva } from '@/common/interfaces/cambioEstadoRes'

export interface HistoricoApiItem {
  cambioEstadoReservaCentralizadaId: number
  colorEstadoAntiguo: string
  colorEstadoNuevo: string
  fechaInicio: string
  fechaFin: string
  urlCartaEnviadaEmail: string | null
  fechaCreacion: string
  usuarioCreacion: string
  codigoReserva: string
  bookingCode: string
  proveedor: string
  estadoAntiguo: string
  estadoNuevo: string
}

export interface HistoricoDetalleApiItem {
  cambioEstadoReservaCentralizadaId: number
  colorEstadoAntiguo: string
  colorEstadoNuevo: string
  reservaCentralizadaDetalleId: number
  fechaInicio: string
  fechaFin: string
  fechaCreacion: string
  usuarioCreacion: string
  actividad: string
  estadoAntiguo: string
  estadoNuevo: string
  urlCartaEnviadaEmail: string | null
}

export interface HistoricoApiResponse {
  historicoCabecera: HistoricoApiItem[]
  historicoDetalles: HistoricoDetalleApiItem[]
  /**
   * Si el codigo buscado corresponde a una reserva, aunque no tenga cambios. Opcional porque el
   * frontend se despliega aparte del backend y contra una API vieja no viene.
   */
  existe?: boolean
}

export interface HistoricoReservaCentralizadaResult {
  cabecera: ICambioEstadoReserva[]
  detalle: ICambioEstadoReserva[]
  ultimoEstado: string
  /** El proveedor de la reserva, tomado del registro mas reciente. */
  proveedor: string
  /** El codigo de booking de la reserva, tomado del registro mas reciente. */
  bookingCode: string
  /**
   * Cuantas reservas centralizadas distintas trae el resultado.
   *
   * Con la busqueda por codigo de booking puede ser mas de una, y entonces `proveedor` y `ultimoEstado`
   * —que salen del registro mas reciente— no representan al conjunto: hay que decir cuantas son en vez
   * de mostrar los de una sola como si fueran los de todas. El booking Opp-82740, por ejemplo, trae tres
   * reservas de tres proveedores distintos.
   */
  reservas: number
  /**
   * Si el codigo buscado corresponde a una reserva, aunque no tenga ningun cambio. `undefined` cuando
   * la API no lo informa, y ahi el mensaje de vacio no puede afirmar ni que existe ni que no.
   */
  existe: boolean | undefined
}

const mapCabeceraToModel = (item: HistoricoApiItem): ICambioEstadoReserva => ({
  CambioEstadoReservaCentralizadaId: item.cambioEstadoReservaCentralizadaId,
  ColorEstadoAntiguo: item.colorEstadoAntiguo,
  ColorEstadoNuevo: item.colorEstadoNuevo,
  FechaInicio: toDate(item.fechaInicio),
  FechaFin: toDate(item.fechaFin),
  UrlCartaEnviadaEmail: item.urlCartaEnviadaEmail,
  FechaCreacion: toDate(item.fechaCreacion),
  UsuarioCreacion: item.usuarioCreacion,
  ReservaCentralizada: {
    CodigoReserva: item.codigoReserva,
    Proveedor: { NombreCompleto: item.proveedor ?? '' },
    ActividadMaestra: { BookingId: 0, BookingCode: item.bookingCode },
  },
  EstadoFisicoAntiguo: { Nombre: item.estadoAntiguo },
  EstadoFisicoNuevo: { Nombre: item.estadoNuevo },
})

const mapDetalleToModel = (item: HistoricoDetalleApiItem): ICambioEstadoReserva => ({
  CambioEstadoReservaCentralizadaId: item.cambioEstadoReservaCentralizadaId,
  ColorEstadoAntiguo: item.colorEstadoAntiguo,
  ColorEstadoNuevo: item.colorEstadoNuevo,
  ReservaCentralizadaDetalleId: item.reservaCentralizadaDetalleId,
  FechaInicio: toDate(item.fechaInicio),
  FechaFin: toDate(item.fechaFin),
  FechaCreacion: toDate(item.fechaCreacion),
  UsuarioCreacion: item.usuarioCreacion,
  ReservaCentralizada: {
    CodigoReserva: '',
    Proveedor: { NombreCompleto: '' },
    ActividadMaestra: { BookingId: 0, BookingCode: '' },
  },
  ReservaCentralizadaDetalle: {
    ActividadId: 0,
    Actividad: {
      Nombre: item.actividad,
      FechaInicio: toDate(item.fechaInicio),
      FechaFin: toDate(item.fechaFin),
    },
  },
  EstadoFisicoAntiguo: { Nombre: item.estadoAntiguo },
  EstadoFisicoNuevo: { Nombre: item.estadoNuevo },
  UrlCartaEnviadaEmail: item.urlCartaEnviadaEmail || null,
})

/**
 * Convierte la respuesta de la API en lo que consume la pantalla.
 *
 * Vive aparte del `createApi` para poder probarse: importar el servicio arrastra `baseQuery` y con el
 * el store entero, que en pruebas no arranca.
 */
export const mapearRespuestaDelHistorico = (
  response: HistoricoApiResponse,
): HistoricoReservaCentralizadaResult => {
  const cabecera = (response.historicoCabecera ?? []).map(mapCabeceraToModel)
  const detalle = (response.historicoDetalles ?? []).map(mapDetalleToModel)

  // La cabecera viene ordenada por fecha descendente, asi que el primero es el mas reciente.
  const masReciente = cabecera.length > 0 ? cabecera[0] : undefined

  const codigos = new Set(
    cabecera.map((x) => x.ReservaCentralizada?.CodigoReserva).filter((x): x is string => Boolean(x)),
  )

  return {
    cabecera,
    detalle,
    ultimoEstado: masReciente?.EstadoFisicoNuevo?.Nombre ?? '',
    proveedor: masReciente?.ReservaCentralizada?.Proveedor?.NombreCompleto ?? '',
    bookingCode: masReciente?.ReservaCentralizada?.ActividadMaestra?.BookingCode ?? '',
    reservas: codigos.size,
    // Con cambios el codigo existe por definicion; sin cambios manda lo que diga la API.
    existe: cabecera.length > 0 || detalle.length > 0 ? true : response.existe,
  }
}
